import { ClientConfig } from '../config/ClientConfig'
import { checkCookie, getCookie } from './CookieStorageService'
import { BlockedMarket, PromoPlayBlockedMarket } from '../config/FeatureConfig'
import { AnnouncementPhase, PhaseInfo } from './phased-announcements/types'

export class FullMarketExitService {
	constructor(private getCurrentTimestamp: () => number = () => Date.now()) {}

	getPhaseInfo(phase: AnnouncementPhase, residentialRegionCode: string, geoLocationRegionCode: string): PhaseInfo {
		if (!this.isFullMarketExitModalsEnabled()) {
			return { enabled: false, phase }
		}

		const lowerCaseGeoLocation = geoLocationRegionCode?.toLowerCase()
		const lowerCaseResidentialLocation = residentialRegionCode?.toLowerCase()
		const allBlockedMarkets = [...ClientConfig.BLOCKED_MARKETS, ...ClientConfig.GEOBLOCKED_MARKETS]

		const relevantMarket = allBlockedMarkets.find(
			(market) =>
				(lowerCaseGeoLocation && market.knownEntries.includes(lowerCaseGeoLocation)) ||
				(lowerCaseResidentialLocation && market.knownEntries.includes(lowerCaseResidentialLocation)),
		)

		if (!relevantMarket) {
			return { enabled: false, phase }
		}

		const phase1StartTimestamp = this.getRegistrationBlockTimestamp(relevantMarket)
		const phase2StartTimestamp = this.getGameplayBlockTimestamp(relevantMarket)
		const phase3StartTimestamp = this.getLoginBlockTimestamp(relevantMarket as BlockedMarket)

		const currentTimestamp = this.getCurrentTimestamp()
		let phaseStartTimestamp = 0
		let isInPhase = false

		switch (phase) {
			case AnnouncementPhase.Phase1:
				phaseStartTimestamp = phase1StartTimestamp
				if (phase1StartTimestamp !== phase2StartTimestamp) {
					isInPhase = currentTimestamp >= phase1StartTimestamp && currentTimestamp < phase2StartTimestamp
				}
				break
			case AnnouncementPhase.Phase2:
				phaseStartTimestamp = phase2StartTimestamp
				isInPhase = currentTimestamp >= phase2StartTimestamp && currentTimestamp < phase3StartTimestamp
				break
			case AnnouncementPhase.Phase3:
				phaseStartTimestamp = phase3StartTimestamp
				isInPhase = currentTimestamp >= phase3StartTimestamp
				break
		}

		return {
			enabled: isInPhase,
			phase,
			market: {
				isoCountry: relevantMarket.isoCountry,
				isoRegion: relevantMarket.isoEntry,
				countryName: relevantMarket.countryName,
				regionName: relevantMarket.regionName,
			},
			startAtUtc: new Date(phaseStartTimestamp).toISOString(),
			ianaTimeZone: relevantMarket.ianaTimeZone,
		}
	}

	isRegistrationBlocked(residentialRegionCode: string, geoLocationRegionCode: string): boolean {
		if (!residentialRegionCode) return false

		return (
			this._isRegistrationBlockedByGeolocation(geoLocationRegionCode) ||
			this._isRegistrationBlockedByResidence(residentialRegionCode)
		)
	}

	isGameplayBlocked(residentialRegionCode: string, geoLocationRegionCode: string): boolean {
		if (!residentialRegionCode) return false

		return (
			this._isGameplayBlockedByGeolocation(geoLocationRegionCode) ||
			this._isGameplayBlockedByResidence(residentialRegionCode)
		)
	}

	getBlockedAtRegistration(): string[] {
		const blockedMarketCodes: string[] = []

		ClientConfig.BLOCKED_MARKETS.filter(
			(market) => this.getRegistrationBlockTimestamp(market) <= this.getCurrentTimestamp(),
		).forEach((market) => blockedMarketCodes.push(...market.knownEntries))

		getCookie(`blocked_markets_at_registration`)
			?.value.split(',')
			.forEach((entry) => {
				const trimmedEntry = entry.trim()
				if (!blockedMarketCodes.includes(trimmedEntry)) {
					blockedMarketCodes.push(trimmedEntry)
				}
			})

		return blockedMarketCodes
	}

	getBlockedAtLogin(): string[] {
		const blockedMarketCodes: string[] = []

		ClientConfig.BLOCKED_MARKETS.filter(
			(market) => this.getLoginBlockTimestamp(market) <= this.getCurrentTimestamp(),
		).forEach((market) => blockedMarketCodes.push(...market.knownEntries))

		getCookie(`blocked_markets_at_login`)
			?.value?.split(',')
			.forEach((entry) => {
				const trimmedEntry = entry.trim()
				if (!blockedMarketCodes.includes(trimmedEntry)) {
					blockedMarketCodes.push(trimmedEntry)
				}
			})

		return blockedMarketCodes
	}

	isDelawareExitModalEnabled(): boolean {
		return ClientConfig.DELAWARE_MARKET_EXIT_MODAL_ENABLED || checkCookie('DELAWARE_MARKET_EXIT_MODAL_ENABLED')
	}

	private _isRegistrationBlockedByGeolocation(geoLocationCode: string): boolean {
		if (!geoLocationCode) return false
		const blockedMarket = ClientConfig.GEOBLOCKED_MARKETS.find((market) =>
			market.knownEntries.includes(geoLocationCode.toLowerCase()),
		)
		return !!blockedMarket && this.getRegistrationBlockTimestamp(blockedMarket) <= this.getCurrentTimestamp()
	}

	private _isRegistrationBlockedByResidence(residentialRegionCode: string): boolean {
		const blockedMarket = ClientConfig.BLOCKED_MARKETS.find((market) =>
			market.knownEntries.includes(residentialRegionCode.toLowerCase()),
		)
		return !!blockedMarket && this.getRegistrationBlockTimestamp(blockedMarket) <= this.getCurrentTimestamp()
	}

	private _isGameplayBlockedByGeolocation(geoLocationCode: string): boolean {
		if (!geoLocationCode) return false
		const blockedMarket = ClientConfig.GEOBLOCKED_MARKETS.find((market) =>
			market.knownEntries.includes(geoLocationCode.toLowerCase()),
		)
		return !!blockedMarket && this.getGameplayBlockTimestamp(blockedMarket) <= this.getCurrentTimestamp()
	}

	private _isGameplayBlockedByResidence(residentialRegionCode: string): boolean {
		const blockedMarket = ClientConfig.BLOCKED_MARKETS.find((market) =>
			market.knownEntries.includes(residentialRegionCode.toLowerCase()),
		)
		return !!blockedMarket && this.getGameplayBlockTimestamp(blockedMarket) <= this.getCurrentTimestamp()
	}

	private getRegistrationBlockTimestamp(marketInfo: BlockedMarket | PromoPlayBlockedMarket): number {
		const cookieName = `P1_BLOCK_${marketInfo.isoCountry}-${marketInfo.isoEntry}_START_TIME`
		const cookie = getCookie(cookieName)
		return cookie ? Date.parse(cookie.value) : marketInfo.blockDate
	}

	private getGameplayBlockTimestamp(marketInfo: BlockedMarket | PromoPlayBlockedMarket): number {
		const cookieName = `P2_BLOCK_${marketInfo.isoCountry}-${marketInfo.isoEntry}_START_TIME`
		const cookie = getCookie(cookieName)
		if (cookie) return Date.parse(cookie.value)

		const registrationBlockDate = new Date(this.getRegistrationBlockTimestamp(marketInfo))
		return registrationBlockDate.setDate(registrationBlockDate.getDate() + marketInfo.blockAllPlayAfterDays)
	}

	private getLoginBlockTimestamp(marketInfo: BlockedMarket): number {
		const cookieName = `P3_BLOCK_${marketInfo.isoCountry}-${marketInfo.isoEntry}_START_TIME`
		const cookie = getCookie(cookieName)
		if (cookie) return Date.parse(cookie.value)

		const registrationBlockDate = new Date(this.getRegistrationBlockTimestamp(marketInfo))
		return registrationBlockDate.setDate(registrationBlockDate.getDate() + marketInfo.blockLoginAfterDays)
	}

	isFullMarketExitModalsEnabled() {
		return ClientConfig.FULL_MARKET_EXIT_MODALS_ENABLED || checkCookie('FULL_MARKET_EXIT_MODALS_ENABLED')
	}
}
